import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { MessageService } from 'primeng/api'
import { NgxSpinnerService } from 'ngx-spinner'
import { AuthService } from '../../services/auth.service'
import { ResponseEntity } from '@app/models/responseEntity.model'
import { User } from '@app/models/user.model'
import { omit } from 'lodash-es'
import { interval, Subscription } from 'rxjs'
import { take } from 'rxjs/operators'

const RESEND_COOLDOWN_SECONDS = 15

@Component({ selector: 'app-validate-pin', templateUrl: './validate-pin.component.html', standalone: false })
export class ValidatePinComponent implements OnInit, OnDestroy {
  title = 'AUTH.APPLY_RESET.TITLE'
  subTitle = 'AUTH.APPLY_RESET.ENTER_THE_PIN_CODE_THAT_SENT_YOUR_PHONE'
  form: FormGroup
  @ViewChildren('inputElem') inputElem: QueryList<ElementRef<HTMLInputElement>>

  /** Component type */
  type: 'RESET_PASSWORD' | 'VERIFY' = 'RESET_PASSWORD'

  /** Resend cooldown */
  resendCooldown = 0
  private cooldownSub: Subscription

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.type = this.route.snapshot.paramMap.get('isVerify') ? 'VERIFY' : 'RESET_PASSWORD'

    if (this.type == 'VERIFY') {
      this.title = 'AUTH.VERIFY_YOUR_IDENTITY'
    } else {
      this.checkRequestedPasswordReset()
    }

    this.initForm()
    this.startCooldown()
    // Use setTimeout to ensure DOM is ready before focusing
    setTimeout(() => this.clearAndSetCursorInStart(), 0)
  }

  ngOnDestroy(): void {
    this.clearCooldown()
  }

  private startCooldown() {
    this.clearCooldown()
    this.resendCooldown = RESEND_COOLDOWN_SECONDS
    this.cooldownSub = interval(1000)
      .pipe(take(RESEND_COOLDOWN_SECONDS))
      .subscribe(() => {
        this.resendCooldown--
        this.cdr.markForCheck()
      })
  }

  private clearCooldown() {
    this.cooldownSub?.unsubscribe()
    this.resendCooldown = 0
  }

  initForm() {
    this.form = this.fb.group({
      code: this.fb.array([
        this.fb.control('', [Validators.required]),
        this.fb.control('', [Validators.required]),
        this.fb.control('', [Validators.required]),
        this.fb.control('', [Validators.required]),
        this.fb.control('', [Validators.required]),
        this.fb.control('', [Validators.required]),
      ]),
    })
  }

  get code() {
    return this.form.controls['code'] as FormArray
  }

  async sendCode() {
    if (this.resendCooldown > 0) return
    this.spinner.show()
    // send request to server
    try {
      let response: ResponseEntity<object> | undefined
      if (this.type == 'RESET_PASSWORD') response = await this.authService.resetPassword().toPromise()
      else {
        this.authService.login(this.authService.form.get('email')?.value as string, this.authService.form.get('password')?.value as string).subscribe()
      }
      this.spinner.hide()
      const success = response!.success
      if (!success) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'could not send code',
        })
      }
      this.clearAndSetCursorInStart()
      this.startCooldown()
    } catch (_e) {
      this.spinner.hide()
    }
  }

  async onValidateClick() {
    const formValue = this.form.value as { code: string[] }
    // here you can have some checks on the form value
    if (this.form.valid) {
      this.spinner.show()
      // send request to server
      try {
        // Fields are always left-to-right, no need to reverse
        const code = formValue.code.join('')

        let response: ResponseEntity<object> | undefined
        if (this.type == 'RESET_PASSWORD') response = await this.authService.validatePin(code).toPromise()
        else response = await this.authService.validate2FA(code).toPromise()
        this.spinner.hide()

        if (response?.success && this.type == 'RESET_PASSWORD') {
          this.authService.pinCode = code
          this.router.navigate(['/auth/apply-reset'])
        } else if (response && !response.errorText && this.type == 'VERIFY') {
          this.setUser(response as unknown as User & { access_token: string })
        } else {
          this.invalidPinCode()
        }
      } catch (_e) {
        this.invalidPinCode()
      }
    } else {
      // show toast error
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill all fields',
      })
    }
  }

  onInput(i: number, event: KeyboardEvent) {
    // Let paste (Ctrl/Cmd+V) through to the (paste) handler
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') return

    event.preventDefault()

    if (event.key === 'Backspace') {
      // If the current field has a value, clear it in place; otherwise step back and clear
      if (this.code.controls[i].value) {
        this.code.controls[i].setValue('')
      } else {
        this.code.controls[i - 1]?.setValue('')
        this.focusInput(i - 1)
      }
    } else if (event.key === 'Delete') {
      this.code.controls[i].setValue('')
    } else if (event.key === 'ArrowLeft') {
      this.focusInput(i - 1)
    } else if (event.key === 'ArrowRight') {
      this.focusInput(i + 1)
    } else if (/^[0-9]$/.test(event.key)) {
      this.code.controls[i]?.setValue(event.key)
      // Always fill left to right: move to next field
      this.focusInput(i + 1)

      // Validate once the last field is filled
      if (i === this.code.length - 1) this.onValidateClick()
    }
    // Any other key is ignored (prevents jumping between fields)
  }

  /**
   * Handle pasting a full code into any field — distribute digits across the boxes.
   */
  onPaste(event: ClipboardEvent) {
    event.preventDefault()
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, this.code.length).split('')
    if (!digits.length) return

    digits.forEach((digit, idx) => this.code.controls[idx]?.setValue(digit))

    const nextEmpty = this.code.controls.findIndex((c) => !c.value)
    this.focusInput(nextEmpty === -1 ? this.code.length - 1 : nextEmpty)

    if (digits.length === this.code.length) this.onValidateClick()
  }

  private focusInput(i: number) {
    const el = this.inputElem?.toArray()[i]?.nativeElement
    el?.focus()
    el?.select?.()
  }

  private checkRequestedPasswordReset() {
    if (!this.authService.requestedPasswordReset) {
      this.router.navigate(['/auth/login'])
    }
  }

  getTabIndex(i: number) {
    return this.code.controls.length + i
  }

  /**
   * Init the user data
   * @param response
   */
  setUser(response: User & { access_token: string }) {
    this.authService.user = omit(response, ['access_token']) as User
    this.authService.setToken(response.access_token)
    this.router.navigate(['/dashboard'])
  }

  /**
   * Return to login page in validity state and the user invalid pinCode 3 times
   */
  checkForReturn() {
    if (this.type == 'VERIFY' && this.authService.countInvalidTimes == 3) {
      this.router.navigate(['/'])
    }
  }

  /**
   * Clear code data and set the cursor on the start
   */
  clearAndSetCursorInStart() {
    this.inputElem?.toArray()[0]?.nativeElement.focus()
  }

  invalidPinCode() {
    this.authService.countInvalidTimes++
    this.checkForReturn()
    this.code.controls.forEach((v) => {
      v.setValue('')
    })

    this.clearAndSetCursorInStart()

    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Invalid Code',
    })
  }
}
